import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';

export interface BrandTab {
  /** Stable identity for this tab; also its React key. */
  value: string;
  label: string;
  /** The brand's logo or monogram, rendered ahead of the label. */
  icon: ReactNode;
}

interface BrandTabsProps {
  tabs: BrandTab[];
  value: string;
  onValueChange: (value: string) => void;
  /** Names the group for assistive tech, e.g. "Brands" or "Competitors". */
  ariaLabel: string;
}

// An underlined tab rather than a pill: the strip is a rail the active brand sits on, so
// only the underline and the weight change between states.
const tabClassName =
  'flex items-center gap-xs border-0 border-b-2 bg-transparent px-sm py-[18px] text-[15px] transition-colors duration-fast ease-out focus-visible:focus-ring';

/**
 * The brand selector shared by Creative Landscape and Competitive Analysis. Both pick one
 * brand to fill a panel below, and the design draws the same rail for each.
 *
 * A tablist with manual activation: arrows move a focus ring across the strip without touching
 * the panel, and Enter, Space or a click is what selects the focused brand. Browsing the names
 * does not yank the panel out from under the one you are reading. Tab reaches the group once and
 * then leaves it, so the strip is a single stop, not one per brand.
 *
 * Sticky, so the brand you are reading about stays named while its panel scrolls. Callers
 * own the selection: neither section can fall back to an internal default, because each has
 * its own rule for what happens when the stored choice is no longer in the list.
 */
export const BrandTabs = ({ tabs, value, onValueChange, ariaLabel }: BrandTabsProps) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === value),
  );

  // Which tab holds focus, kept apart from the selected one so arrowing can browse without
  // committing. A selection from anywhere (click, or the caller) pulls focus back onto it.
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex);
  useEffect(() => {
    setFocusedIndex(selectedIndex);
  }, [selectedIndex]);

  const moveFocus = (index: number) => {
    setFocusedIndex(index);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveFocus((index + 1) % tabs.length);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus((index - 1 + tabs.length) % tabs.length);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(tabs.length - 1);
        break;
      // Enter and Space are what commit the focused brand; the browser fires click for both on a
      // button, so selection is handled there rather than duplicated here.
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="sticky top-0 z-[5] flex items-center gap-xs overflow-x-auto border-b border-border-light bg-background px-2xs"
    >
      {tabs.map((tab, index) => {
        const isActive = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            // Roving tabindex follows focus, not selection: Tab lands wherever the focus ring
            // was left, and arrows move from there.
            tabIndex={index === focusedIndex ? 0 : -1}
            onClick={() => onValueChange(tab.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={clsx(
              tabClassName,
              isActive
                ? 'border-b-foreground font-semibold text-foreground'
                : 'border-b-transparent font-medium text-muted-foreground',
            )}
          >
            {/* The logo is decorative here: the label beside it already names the tab, so
                hiding it keeps the brand from being announced twice. */}
            <span aria-hidden="true" className="flex items-center">
              {tab.icon}
            </span>
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
